/**
 * Chapter 3 — lists: a name rule tags a list, a group collects tagged lists
 * and orders them biggest-first with the engine's own comparator, Today and
 * Next land, and two containers sharing the group keep their own place.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ArrowRight, Tag } from 'lucide-react';

import { compareListsByDefaultOrder } from '@trafficpulse/shared';

import { Badge } from '@/components/ui';

import { ContainerChip } from '@/features/drop-generator/components/shared/ContainerChip';
import { CLEARANCE_CLICKS, CLICKERS_LISTS, WARM_UP } from '../../fixtures';
import { Strike } from '../../motion/Accents';
import { Enter, Node } from '../../motion/Node';
import { Illustration } from '../../motion/Illustration';
import { tick } from '../../motion/use-motion';

const CYCLE = 12;
const ORDERED = [...CLICKERS_LISTS].sort(compareListsByDefaultOrder);
const LABEL = 'w-14 shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground';
const leads = (n: number | null) => (n === null ? 'size unknown' : `${n.toLocaleString('en-US')} numbers`);

/** A Today/Next mark that lights at its tick (a compact Node). */
const Mark: FC<{ at: number; children: string }> = ({ at, children }) => (
  <span
    className="inline-block rounded border border-primary/40 bg-primary/10 px-1 text-[10px] font-semibold uppercase tracking-wider text-primary opacity-55 animate-dg-tut-step motion-reduce:animate-none motion-reduce:opacity-100"
    style={tick(at)}
  >
    {children}
  </span>
);

export const ListsIllustration: FC = () => (
  <Illustration cycleSeconds={CYCLE}>
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Node at={0}>
        <span className="font-mono">AU_ACE_FTD_ALL TIME</span>
      </Node>
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60" />
      <Node at={1}>name contains <span className="font-mono">FTD</span></Node>
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60" />
      <Node at={2} className="border-success/40">
        <Tag className="h-3 w-3 text-success" /> tagged <span className="font-mono">FTD</span>
      </Node>
    </div>
    <p className="mt-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      the group "clickers" — biggest first
    </p>
    <div className="mt-1 flex flex-col gap-1 text-xs">
      {ORDERED.map((list, i) => (
        <Enter key={list.wiserId} at={3 + i * 0.6}>
          <div className="flex items-center gap-2 rounded border border-border/60 bg-background px-2 py-1">
            <span className={LABEL}>
              {i === 0 ? <Mark at={7}>Today</Mark> : i === 1 ? <Mark at={8}>Next</Mark> : `${i + 1}th`}
            </span>
            <span className="min-w-0 grow truncate font-mono">{list.name}</span>
            <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{leads(list.leadCount)}</span>
          </div>
        </Enter>
      ))}
    </div>
    <div className="mt-3 grid gap-2 text-xs md:grid-cols-2">
      <Enter at={9}>
        <div className="rounded border border-border/60 bg-background p-2">
          <ContainerChip name={WARM_UP.name} color={WARM_UP.color} />
          <p className="mt-1 font-mono"><span className={LABEL}>Today</span> {ORDERED[0].name}</p>
        </div>
      </Enter>
      <Enter at={10}>
        <div className="rounded border border-border/60 bg-background p-2">
          <ContainerChip name={CLEARANCE_CLICKS.name} color={CLEARANCE_CLICKS.color} />
          <p className="mt-1 font-mono"><span className={LABEL}>Today</span> {ORDERED[1].name}</p>
          <p className="mt-0.5 flex items-center gap-2 font-mono text-muted-foreground">
            <span className={LABEL} />
            <Strike at={11}>{ORDERED[0].name}</Strike>
            <Badge tone="destructive">terrible</Badge>
          </p>
        </div>
      </Enter>
    </div>
    <p className="mt-2 text-[11px] text-muted-foreground">
      the same group, two containers — one burned a list, the other still uses it
    </p>
  </Illustration>
);
